import React, { useState, useEffect } from "react";
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
} from "lucide-react";
import axiosInstance from "@/axios";
import Modal from "@/pages/Other/Modal";

const emptyForm = {
  id: "",
  page: "",
  data: "{}",
};

const JsonsPageData = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("create"); // create | edit | view
  const [formData, setFormData] = useState(emptyForm);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/json-page");
      setPages(response.data || []);
    } catch (error) {
      console.error("Error fetching pages:", error);
      alert("Failed to fetch pages");
    } finally {
      setLoading(false);
    }
  };

  const filtered = pages.filter((page) =>
    page.page.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openCreate = () => {
    setFormData(emptyForm);
    setModalType("create");
    setShowModal(true);
  };

  const openEdit = (page) => {
    setFormData(page);
    setModalType("edit");
    setShowModal(true);
  };

  const openView = (page) => {
    setSelectedItem(page);
    setModalType("view");
    setShowModal(true);
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (modalType === "create") {
        await axiosInstance.post("/json-page", formData);
        alert("Page created successfully");
      } else if (modalType === "edit") {
        await axiosInstance.put(`/json-page/${formData.id}`, formData);
        alert("Page updated successfully");
      }
      setShowModal(false);
      fetchPages();
    } catch (error) {
      console.error("Error saving page:", error);
      alert("Failed to save page");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this page?")) return;
    try {
      setLoading(true);
      await axiosInstance.delete(`/json-page/${id}`);
      alert("Page deleted successfully");
      fetchPages();
    } catch (error) {
      console.error("Error deleting page:", error);
      alert("Failed to delete page");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} items?`)) return;

    try {
      setLoading(true);
      await Promise.all(
        selectedIds.map((id) => axiosInstance.delete(`/json-page/${id}`))
      );
      alert("Bulk delete successful");
      fetchPages();
      setSelectedIds([]);
    } catch (error) {
      console.error("Bulk delete error:", error);
      alert("Bulk delete failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-5 mt-3">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold p-2">JSON Pages</h1>

        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by page name..."
              className="pl-9 pr-3 py-2 border rounded-lg"
            />
          </div>

          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="bg-red-500 text-white px-4 py-2 rounded-md"
            >
              Delete Selected ({selectedIds.length})
            </button>
          )}

          <button
            onClick={openCreate}
            className="bg-[#264b82] text-white px-4 py-2 rounded-md flex items-center gap-2"
          >
            <Plus className="h-4 w-4" /> New Page
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
          </div>
        ) : (
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-3">
                  <input
                    type="checkbox"
                    checked={
                      selectedIds.length > 0 &&
                      selectedIds.length === filtered.length
                    }
                    onChange={(e) => {
                      if (e.target.checked)
                        setSelectedIds(filtered.map((i) => i.id));
                      else setSelectedIds([]);
                    }}
                  />
                </th>
                <th className="text-left p-3">Page Name</th>
                <th className="text-left p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((page) => (
                <tr key={page.id} className="border-t hover:bg-gray-50">
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(page.id)}
                      onChange={(e) => {
                        if (e.target.checked)
                          setSelectedIds((prev) => [...prev, page.id]);
                        else
                          setSelectedIds((prev) =>
                            prev.filter((x) => x !== page.id)
                          );
                      }}
                    />
                  </td>
                  <td className="p-3">{page.page}</td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openView(page)}
                        title="View"
                        className="p-2 hover:bg-gray-100 rounded"
                      >
                        <Eye className="h-4 w-4 text-gray-700" />
                      </button>
                      <button
                        onClick={() => openEdit(page)}
                        title="Edit"
                        className="p-2 hover:bg-gray-100 rounded"
                      >
                        <Edit2 className="h-4 w-4 text-indigo-600" />
                      </button>
                      <button
                        onClick={() => handleDelete(page.id)}
                        title="Delete"
                        className="p-2 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {filtered.length === 0 && !loading && (
          <div className="p-6 text-center text-gray-500">No pages found.</div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-[#264b82] px-6 py-3 text-white rounded-t">
            <h3 className="text-lg font-semibold">
              {modalType === "create" && "Create Page"}
              {modalType === "edit" && "Edit Page"}
              {modalType === "view" && "View Page"}
            </h3>
          </div>

          <div className="p-6 max-h-[70vh] overflow-auto">
            {modalType === "view" && selectedItem && (
              <div className="space-y-3">
                <div>
                  <strong>Page Name:</strong> {selectedItem.page}
                </div>
                <div>
                  <strong>Data:</strong>
                  <pre className="mt-2 p-3 bg-gray-50 rounded text-sm">
                    {JSON.stringify(JSON.parse(selectedItem.data), null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {(modalType === "create" || modalType === "edit") && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Page Name</label>
                  <input
                    name="page"
                    value={formData.page}
                    onChange={handleInput}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Data (JSON)</label>
                  <textarea
                    name="data"
                    value={formData.data}
                    onChange={handleInput}
                    className="w-full px-3 py-2 border rounded-lg"
                    rows="10"
                    required
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 bg-gray-200 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#264b82] text-white rounded-md"
                  >
                    {modalType === "create" && "Create Page"}
                    {modalType === "edit" && "Update Page"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default JsonsPageData;